import type { ProductLookupRow } from '@graphql/queries/PerceptionModelQueries';

/**
 * What a norm set tastes like, as one number per token.
 *
 * A norm set is a comparison basis for flavour, so the useful summary of one is
 * sensory rather than geometric. Mean distance is how the ranking was made; it
 * says nothing about what was ranked.
 */

export const TOKEN_FAMILIES = ['flavor', 'texture', 'sensation'] as const;

export type TokenFamily = (typeof TOKEN_FAMILIES)[number];

export const FAMILY_LABEL: Record<TokenFamily, string> = {
  flavor: 'Flavor',
  texture: 'Texture',
  sensation: 'Sensation',
};

const FAMILY_FIELD: Record<TokenFamily, keyof ProductLookupRow> = {
  flavor: 'tokensFlavor',
  texture: 'tokensTexture',
  sensation: 'tokensSensation',
};

export type TokenSummary = {
  token: string;
  /** members carrying it above the export threshold */
  members: number;
  /** members / total, 0..1 */
  coverage: number;
  /** no anchor carries it — drift from what the comparison was anchored on */
  novel: boolean;
};

const tokensOf = (
  row: ProductLookupRow | undefined,
  family: TokenFamily,
): Record<string, number> | null =>
  (row?.[FAMILY_FIELD[family]] as Record<string, number> | null) ?? null;

/**
 * Coverage rather than mean prevalence.
 *
 * `tokens_*` stores only entries above an export threshold, so a token missing
 * from a product means "below the line", not "zero". Averaging prevalence with
 * absent-as-zero understates every token, and understates it further the larger
 * the norm set gets — the one direction that matters, since a norm set is
 * usually a hundred products and its anchors are three.
 *
 * Counting how many members carry a token at all is immune to that. It still
 * inherits the threshold — a token just under the line everywhere reads as
 * absent — which is why the caption says so rather than the number pretending
 * otherwise.
 */
export const tokenProfile = (
  members: ProductLookupRow[],
  anchors: ProductLookupRow[],
  family: TokenFamily,
  limit = 6,
): TokenSummary[] => {
  // Products the export had nothing to say about stay in the denominator.
  // Dropping them would inflate every figure by shrinking the population to
  // exactly the products that agree with it.
  const total = members.length;
  if (total === 0) return [];

  const counts = new Map<string, number>();

  members.forEach((row) => {
    const tokens = tokensOf(row, family);
    if (!tokens) return;
    Object.keys(tokens).forEach((token) => {
      counts.set(token, (counts.get(token) ?? 0) + 1);
    });
  });

  const anchorTokens = new Set<string>();
  anchors.forEach((row) => {
    const tokens = tokensOf(row, family);
    if (!tokens) return;
    Object.keys(tokens).forEach((token) => anchorTokens.add(token));
  });

  return Array.from(counts.entries())
    .map(([token, count]) => ({
      token,
      members: count,
      coverage: count / total,
      novel: !anchorTokens.has(token),
    }))
    .sort((a, b) =>
      a.members !== b.members
        ? b.members - a.members
        : // Stable across renders: equal coverage would otherwise order by
          // whatever the Map happened to hold.
          a.token.localeCompare(b.token),
    )
    .slice(0, limit);
};

/** Every family at once, in a fixed order so the rows never reshuffle. */
export const sensorySignature = (
  members: ProductLookupRow[],
  anchors: ProductLookupRow[],
  limit = 6,
): Array<{ family: TokenFamily; tokens: TokenSummary[] }> =>
  TOKEN_FAMILIES.map((family) => ({
    family,
    tokens: tokenProfile(members, anchors, family, limit),
  }));
